import React, { useEffect, useState } from 'react';
import ImmutablePropTypes from 'react-immutable-proptypes';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import { useIsEmbedded } from 'hooks/useIsEmbedded';
import PropTypes from 'prop-types';

import EditMode from 'features/timesheets/TimesheetsPage/TimecardModal/EditMode';
import ReadMode from 'features/timesheets/TimesheetsPage/TimecardModal/ReadMode';

import BackToTimesheets from 'components/BackToTimesheets';

import { toI18n } from 'util/i18n';

import StatusBar from './StatusBar';
import { handleResolveIssuesTracking } from './utils';

const TimecardModal = ({
  addLocationRole,
  addTimebreak,
  breakPenaltiesEnabled,
  canManageTimecards,
  closeModal,
  currentShiftIssues,
  deleteTimecard,
  dismissMissedBreaks,
  employeeOptions,
  editableShift,
  editMode,
  expandBreaks,
  hasTimecard,
  timesheetApprovalEnabled,
  hasTimecardLockPermission,
  hideTipsSection,
  isLandscape,
  isLockDisabled,
  isMobile,
  isSubmitting,
  mandatedBreaks,
  modal,
  onChangeShift,
  onChangeTimebreak,
  onChangeFormError,
  onDisableEditMode,
  onSave,
  onUpdateLockedStatus,
  paywallVersion,
  previousTrackingKey,
  removeTimebreak,
  roleOptions,
  shift,
  startingPath,
  submitChanges,
  validationErrors,
  changesMade,
  setChangesMade,
  focusClockOut = false,
}) => {
  const [previousShiftIssues, setPreviousShiftIssues] = useState([]);
  const isEmbedded = useIsEmbedded();

  useEffect(() => {
    if (modal === 'opened') {
      handleResolveIssuesTracking(
        currentShiftIssues,
        previousShiftIssues,
        previousTrackingKey || shift.get('id'),
        startingPath,
        'v3'
      );
    }

    setPreviousShiftIssues(currentShiftIssues);
  }, [
    modal,
    currentShiftIssues,
    previousShiftIssues,
    previousTrackingKey,
    shift,
    startingPath,
  ]);

  return (
    <Box
      h={isMobile || isEmbedded ? '100%' : null}
      w="100%"
      flex={isEmbedded}
      column={isEmbedded}
    >
      {shift.get('status') && (
        <StatusBar isMobile={isMobile} status={shift.get('status')} />
      )}

      {isEmbedded && (
        <Box mv={32}>
          <BackToTimesheets pl={32} onClick={closeModal} />
          <Box pt={32} pl={32}>
            <Text variant="heading2" color="mono900">
              {!editMode && toI18n('timecard_modal_v3.embedded.view_time_card')}
              {editMode &&
                !editableShift.get('id') &&
                toI18n('timecard_modal_v3.embedded.add_time_card')}
              {editMode &&
                editableShift.get('id') &&
                toI18n('timecard_modal_v3.embedded.edit_time_card')}
            </Text>
          </Box>
        </Box>
      )}

      <Box h="100%" justifyContentCenter={isEmbedded}>
        {editMode ? (
          <EditMode
            addLocationRole={addLocationRole}
            addTimebreak={addTimebreak}
            breakPenaltiesEnabled={breakPenaltiesEnabled}
            closeModal={closeModal}
            dateStr={editableShift.get('date')}
            deleteTimecard={deleteTimecard}
            editableShift={editableShift}
            employeeOptions={employeeOptions}
            hasTimecard={hasTimecard}
            hideTipsSection={hideTipsSection}
            isMobile={isMobile}
            isSubmitting={isSubmitting}
            mandatedBreaks={mandatedBreaks}
            onChangeShift={onChangeShift}
            onChangeTimebreak={onChangeTimebreak}
            onChangeFormError={onChangeFormError}
            onDisableEditMode={onDisableEditMode}
            onSave={onSave}
            removeTimebreak={removeTimebreak}
            roleOptions={roleOptions}
            employeeName={shift.get('short_name')}
            validationErrors={validationErrors}
            changesMade={changesMade}
            setChangesMade={setChangesMade}
            shift={shift}
            canManageTimecards={canManageTimecards}
            focusClockOut={focusClockOut}
          />
        ) : (
          <ReadMode
            addTimebreak={addTimebreak}
            breakPenaltiesEnabled={breakPenaltiesEnabled}
            closeModal={closeModal}
            canManageTimecards={canManageTimecards}
            dismissMissedBreaks={dismissMissedBreaks}
            editMode={editMode}
            expandBreaks={expandBreaks}
            timesheetApprovalEnabled={timesheetApprovalEnabled}
            hasTimecardLockPermission={hasTimecardLockPermission}
            hideTipsSection={hideTipsSection}
            isLandscape={isLandscape}
            isLockDisabled={isLockDisabled}
            isMobile={isMobile}
            onChangeShift={onChangeShift}
            onUpdateLockedStatus={onUpdateLockedStatus}
            paywallVersion={paywallVersion}
            shift={shift}
            submitChanges={submitChanges}
            setChangesMade={setChangesMade}
          />
        )}
      </Box>
    </Box>
  );
};

TimecardModal.propTypes = {
  addTimebreak: PropTypes.func.isRequired,
  canManageTimecards: PropTypes.bool.isRequired,
  currentShiftIssues: PropTypes.array,
  dismissMissedBreaks: PropTypes.func.isRequired,
  editMode: PropTypes.bool.isRequired,
  isLockDisabled: PropTypes.bool.isRequired,
  isMobile: PropTypes.bool,
  isSubmitting: PropTypes.bool.isRequired,
  modal: PropTypes.string,
  onChangeShift: PropTypes.func.isRequired,
  onChangeTimebreak: PropTypes.func.isRequired,
  onDisableEditMode: PropTypes.func.isRequired,
  onUpdateLockedStatus: PropTypes.func.isRequired,
  paywallVersion: PropTypes.string,
  previousTrackingKey: PropTypes.number,
  removeTimebreak: PropTypes.func.isRequired,
  shift: ImmutablePropTypes.map.isRequired,
  startingPath: PropTypes.string,
  validationErrors: PropTypes.object.isRequired,
  changesMade: PropTypes.bool.isRequired,
  setChangesMade: PropTypes.func.isRequired,
  focusClockOut: PropTypes.bool,
};

export default TimecardModal;
